import {
  calculatePublicTechnicalDraftWithHashes,
  calculateResolvedWithHashes,
  resolveReferenceCaseForVerification,
  type HashedPublicTechnicalDraftResult,
  type HashedCalculationResult,
  type PublicTechnicalDraftInput,
  type ReferenceRuleset,
} from "@rcprofessionale/calc-core";

import publicTechnicalFixtureJson from "../../../docs/calcolatore/casi-doro/dm2016-allegato-i13-agenzia-demanio-palazzo-fienga.json";
import rulesetJson from "../../../docs/calcolatore/rulesets/dm140-official-example-v1.draft.json";
import {
  buildDm2016CorpusStatus,
  type Dm2016CorpusStatus,
} from "./corpus-status.js";

export interface VerificationComponent {
  readonly componentId: string;
  readonly categoryCode: string;
  readonly label: string;
  readonly valueV: string;
  readonly complexityG: string;
  readonly qTotal: string;
  readonly parameterP: string;
  readonly parameterPPercentDisplay: string;
  readonly compensationRaw: string;
  readonly compensationRounded: string;
  readonly expectedCompensation: string;
  readonly matchesOfficialExample: boolean;
  readonly services: readonly {
    readonly code: string;
    readonly description: string;
    readonly qValue: string;
    readonly sourceLocator: string;
  }[];
  readonly sourceLocator: string;
  readonly crosscheckStatus:
    | "within_range"
    | "conflict_requires_approval"
    | "ambiguous_requires_selection";
}

export interface VerificationModel {
  readonly ruleset: ReferenceRuleset;
  readonly result: HashedCalculationResult;
  readonly components: readonly VerificationComponent[];
  readonly allValuesMatch: boolean;
  readonly publicTechnical: {
    readonly result: HashedPublicTechnicalDraftResult;
    readonly expectedTotal: string;
    readonly allValuesMatch: boolean;
    readonly sourceLabel: string;
  };
  readonly normativeCorpus: Dm2016CorpusStatus;
}

interface PublicTechnicalFixture {
  readonly source: { readonly publisher: string; readonly title: string };
  readonly input: PublicTechnicalDraftInput;
  readonly expected: {
    readonly line_compensation: Readonly<Record<string, string>>;
    readonly compensation: string;
    readonly bim_increment: string;
    readonly compensation_after_bim: string;
    readonly expenses: string;
    readonly total: string;
  };
}

export async function buildVerificationModel(): Promise<VerificationModel> {
  const { ruleset, calculation } =
    resolveReferenceCaseForVerification(rulesetJson);
  const result = await calculateResolvedWithHashes(calculation);

  const categories = new Map(ruleset.categories.map((item) => [item.code, item]));
  const services = new Map(ruleset.services.map((item) => [item.code, item]));

  const components = result.components.map((component, index) => {
    const reference = ruleset.reference_case.components[index];
    if (reference === undefined) throw new Error("Componente di riferimento mancante");
    const category = categories.get(reference.category_code);
    if (category === undefined) throw new Error("Categoria di riferimento mancante");

    return {
      componentId: component.componentId,
      categoryCode: reference.category_code,
      label: component.label,
      valueV: component.valueV,
      complexityG: component.complexityG,
      qTotal: component.qTotal,
      parameterP: component.parameterP,
      parameterPPercentDisplay: component.parameterPPercentDisplay,
      compensationRaw: component.compensationRaw,
      compensationRounded: component.compensationRounded,
      expectedCompensation: reference.expected_compensation,
      matchesOfficialExample:
        component.compensationRounded === reference.expected_compensation,
      services: reference.service_codes.map((serviceCode) => {
        const service = services.get(serviceCode);
        const qValue = service?.q_by_group[category.group];
        if (service === undefined || qValue === null || qValue === undefined) {
          throw new Error("Prestazione di riferimento mancante");
        }
        return {
          code: service.code,
          description: service.description,
          qValue,
          sourceLocator: service.source_locator,
        };
      }),
      sourceLocator: category.source_locator,
      crosscheckStatus: category.z1_crosscheck.status,
    } satisfies VerificationComponent;
  });

  const allValuesMatch =
    components.every((component) => component.matchesOfficialExample) &&
    result.totals.valueV === ruleset.reference_case.expected_value_total &&
    result.totals.compensationRounded ===
      ruleset.reference_case.expected_compensation_total;

  const publicFixture = publicTechnicalFixtureJson as PublicTechnicalFixture;
  const publicResult = await calculatePublicTechnicalDraftWithHashes(
    publicFixture.input,
  );
  const publicLinesMatch = publicResult.lines.every(
    (line) => publicFixture.expected.line_compensation[line.code]
      === line.compensationRounded,
  );
  const publicTotalsMatch =
    publicResult.totals.compensationRounded === publicFixture.expected.compensation
    && publicResult.totals.bimIncrement === publicFixture.expected.bim_increment
    && publicResult.totals.compensationAfterBim
      === publicFixture.expected.compensation_after_bim
    && publicResult.totals.expenses === publicFixture.expected.expenses
    && publicResult.totals.totalBeforeTaxesAndContributions
      === publicFixture.expected.total;
  const publicAllValuesMatch = publicLinesMatch && publicTotalsMatch;
  if (!publicAllValuesMatch) {
    throw new Error("Il caso d’oro pubblico dell’Agenzia del Demanio non coincide");
  }

  return {
    ruleset,
    result,
    components,
    allValuesMatch,
    publicTechnical: {
      result: publicResult,
      expectedTotal: publicFixture.expected.total,
      allValuesMatch: publicAllValuesMatch,
      sourceLabel: `${publicFixture.source.publisher} · ${publicFixture.source.title}`,
    },
    normativeCorpus: await buildDm2016CorpusStatus(),
  };
}
